import React, { useState, useMemo, useEffect } from 'react';
import useAppStore from '../store/index'; 
import { usePersonalDataSelector } from '../store/usePersonalDataSelector';
import { useTeamBonus } from '../store/hooks/useTeamBonus';
import EditableField from '../components/EditableField';
import { Section, SectionTitle } from '../components/UI';
import { NobleProvisionDropdown } from '../components/NobleProvisionDropdown';
import { applyProvisionChange, getProvisionOptions, type ProvisionField } from '../utils/leistungsprovision';
import { TeamBonusModeToggle, TeamBonusConfigButton, PETeamBonusModal } from '../components/teamBonus';
import { ZusatzvereinbarungModal, type LeistungslohnVariant } from '../components/leistungslohn';
import ItBzLohnberechnungInfoModal from '../components/personal/ItBzLohnberechnungInfoModal';
import { isItBzMode, isAtMode } from '../utils/personnelCostAdapter';

// --- ICONS & HILFSKOMPONENTEN ---
const IconCheck = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> );
const IconAlert = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg> );
const IconCalculator = () => ( <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="8" y1="6" x2="16" y2="6"></line><line x1="16" y1="14" x2="16" y2="14"></line><line x1="12" y1="14" x2="12" y2="14"></line><line x1="8" y1="14" x2="8" y2="14"></line><line x1="16" y1="18" x2="16" y2="18"></line><line x1="12" y1="18" x2="12" y2="18"></line><line x1="8" y1="18" x2="8" y2="18"></line></svg> );

// --- ZELLEN KOMPONENTEN ---

const ResultCell = ({ value, formula, type, currency, suffix, isGood, isBad, tooltip, decimalPlaces }: any) => {
    let textClass = "text-pe-accent-text dark:text-[#E6C299]";
    let bgClass = "bg-stone-200/60 dark:bg-slate-800/50";
    if (isGood) textClass = "text-green-700 dark:text-green-400";
    if (isBad) { textClass = "text-red-600 dark:text-red-400"; bgClass = "bg-red-100/70 dark:bg-red-900/20"; }

    return (
        <div 
            className={`w-full h-full ${bgClass} ${textClass} flex items-center justify-center relative`}
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            <EditableField value={value} readOnly type={type} currency={currency} suffix={suffix} decimalPlaces={decimalPlaces} onChange={() => {}} />
            {formula && <div className="absolute bottom-0 right-0 text-[8px] opacity-30 px-1 pointer-events-none">{formula}</div>}
        </div>
    );
};

const DropdownCell = ({ value, options, onChange, tooltip }: { value: string; options: string[]; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; tooltip?: string }) => (
    <div 
        className="w-full h-full relative bg-stone-100/60 dark:bg-black/40 group flex"
        data-tooltip-id="app-tooltip" 
        data-tooltip-content={tooltip}
    >
        <select 
            value={value} 
            onChange={onChange} 
            className="w-full h-full bg-transparent text-pe-accent-text dark:text-[#E6C299] text-center text-sm font-bold appearance-none cursor-pointer focus:outline-none focus:bg-stone-100 dark:focus:bg-slate-900 px-2"
        >
            {options.map(opt => <option key={opt} value={opt} className="bg-pe-card dark:bg-black text-pe-accent-text dark:text-[#E6C299]">{opt}</option>)}
        </select>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-pe-accent-text dark:text-[#E6C299] opacity-50 text-[10px]">▼</div>
    </div>
);

const MindestlohnIndicator = ({ stundenlohn, mindestlohn, taetigkeit, countryId, tooltip, brutto }: any) => {
    const ausgeschlossene = ['Azubi', 'Prod. Azubi', 'Unprod. Anteil', 'Bitte auswählen'];
    
    if (countryId !== 'DE' || ausgeschlossene.includes(taetigkeit)) return <div className="h-full w-full flex items-center justify-center bg-stone-200/60 dark:bg-slate-800/50 text-stone-400 dark:text-slate-600 text-xs">--</div>;
    
    const numStd = parseFloat(String(stundenlohn).replace(',', '.'));
    const numMin = parseFloat(String(mindestlohn).replace(',', '.'));
    if (isNaN(numStd) || isNaN(numMin) || numMin <= 0) return <div className="h-full w-full bg-stone-200/60 dark:bg-slate-800/50 text-stone-500 dark:text-slate-500">--</div>;
    
    const isOk = numStd >= (numMin - 0.0001);
    
    return (
        <div 
            className={`h-full w-full flex items-center justify-center transition-colors ${isOk ? 'bg-stone-200/60 dark:bg-slate-800/50 text-green-700 dark:text-green-400' : 'bg-red-100/70 dark:bg-red-900/30 text-red-600 dark:text-red-400 animate-pulse'}`}
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            {isOk ? <IconCheck /> : <IconAlert />}
        </div>
    );
};

// --- SECTIONS ---

const LeistungslohnProvisionen = ({ onOpenTeamBonusModal }: { onOpenTeamBonusModal: () => void }) => {
    const { leistungsprovision } = usePersonalDataSelector();
    const updateNestedClientData = useAppStore(state => state.updateNestedClientData);
    const { isTeamMode, bonusMode, setBonusMode } = useTeamBonus();
    const [zusatzModalOpen, setZusatzModalOpen] = useState(false);
    const [zusatzVariant, setZusatzVariant] = useState<LeistungslohnVariant>('mono');
    const safeData = leistungsprovision || { erstesHalbjahr: { provisionDienstleistung: 10, provisionVerkauf: 10 }, zweitesHalbjahr: { provisionDienstleistung: 10, provisionVerkauf: 10 } };
    
    const handleChange = (halbjahr: string, typ: ProvisionField, val: string) => {
        const halbjahrData = safeData[halbjahr as keyof typeof safeData] || {};
        updateNestedClientData(
            'leistungsprovision',
            halbjahr,
            applyProvisionChange(halbjahrData, typ, parseInt(val, 10))
        );
    };

    const Selector = ({ title, objKey }: { title: string, objKey: 'erstesHalbjahr' | 'zweitesHalbjahr' }) => (
        <div className="flex flex-col gap-4">
            <h4 className="text-pe-accent-text dark:text-[#E6C299] text-xs font-bold uppercase tracking-wider border-l-2 border-[#E6C299] pl-2">
                {title}
            </h4>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <span className="text-pe-muted dark:text-slate-400 text-[10px] uppercase tracking-wider pl-1">Dienstleistung</span>
                    <div data-tooltip-id="app-tooltip" data-tooltip-content="Anteil am Dienstleistungs-Umsatz. Zusammen mit Verkauf max. 60%.">
                        <NobleProvisionDropdown 
                            value={safeData[objKey]?.provisionDienstleistung || 10} 
                            options={getProvisionOptions(safeData[objKey] || {}, 'provisionDienstleistung')}
                            onChange={val => handleChange(objKey, 'provisionDienstleistung', val)}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <span className="text-pe-muted dark:text-slate-400 text-[10px] uppercase tracking-wider pl-1">Verkauf</span>
                    <div data-tooltip-id="app-tooltip" data-tooltip-content="Anteil am Produkt-Verkaufsumsatz. Zusammen mit Dienstleistung max. 60%.">
                        <NobleProvisionDropdown 
                            value={safeData[objKey]?.provisionVerkauf || 10} 
                            options={getProvisionOptions(safeData[objKey] || {}, 'provisionVerkauf')}
                            onChange={val => handleChange(objKey, 'provisionVerkauf', val)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-pe-card dark:bg-slate-900/30 border border-pe-border dark:border-slate-700/50 rounded-xl p-5 shadow-lg relative">
             <div className="flex flex-wrap items-center gap-3 mb-6 border-b border-pe-border dark:border-slate-700/50 pb-4">
                <div className="p-2 bg-pe-tile-header dark:bg-slate-800 rounded-lg text-pe-accent-text dark:text-[#E6C299]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </div>
                <h3 className="text-xl font-bold text-pe-text dark:text-white">Provisionen</h3>

                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                    <TeamBonusModeToggle mode={bonusMode} onChange={setBonusMode} />
                    <TeamBonusConfigButton visible={isTeamMode} onClick={onOpenTeamBonusModal} />
                    <button
                        type="button"
                        onClick={() => {
                            setZusatzVariant(bonusMode === 'team' ? 'team' : 'mono');
                            setZusatzModalOpen(true);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg border border-pe-border dark:border-slate-700 bg-pe-tile-header dark:bg-slate-800 text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/50 hover:text-pe-accent-text dark:hover:text-[#E6C299] transition-colors"
                    >
                        Zusatzvereinbarung (PDF) &amp; Info
                    </button>
                </div>
            </div>
            <div className="space-y-8 flex-grow">
                <Selector title="1. Halbjahr" objKey="erstesHalbjahr" />
                <Selector title="2. Halbjahr" objKey="zweitesHalbjahr" />
            </div>

            <ZusatzvereinbarungModal
                isOpen={zusatzModalOpen}
                onClose={() => setZusatzModalOpen(false)}
                variant={zusatzVariant}
                onVariantChange={setZusatzVariant}
            />
        </div>
    );
};

const KalkulationsCockpit = ({ currency }: { currency: string }) => {
    const settings = useAppStore(state => state.settings);
    const Wochensatz = settings?.global?.Wochensatz || 4.33;
    
    const [basis, setBasis] = useState({ stdTag: 6, tageMonat: 2 }); 
    const [sz1, setSz1] = useState({ stdLohn: 18, woStd: 35 });      
    const [sz2, setSz2] = useState({ brutto: 1700, woStd: 30 });     
    const [sz3, setSz3] = useState({ brutto: 974.25, stdLohn: 15.50 });

    const schnittWoche = useMemo(() => (Wochensatz > 0) ? (basis.stdTag * basis.tageMonat) / Wochensatz : 0, [basis, Wochensatz]);
    const sz1_brutto = useMemo(() => sz1.stdLohn * sz1.woStd * Wochensatz, [sz1, Wochensatz]);
    const sz2_stdLohn = useMemo(() => (sz2.woStd * Wochensatz) > 0 ? sz2.brutto / (sz2.woStd * Wochensatz) : 0, [sz2, Wochensatz]);
    const sz3_woStd = useMemo(() => (sz3.stdLohn * Wochensatz) > 0 ? sz3.brutto / (sz3.stdLohn * Wochensatz) : 0, [sz3, Wochensatz]);

    const Group = ({ label, children }: any) => (
        <div className="flex flex-col">
            <span className="text-pe-muted dark:text-slate-400 text-[10px] uppercase tracking-wider mb-1 block pl-1">{label}</span>
            {children}
        </div>
    );

    const Inp = ({ val, set, field, type="number", curr, suffix, dp, tooltip }: any) => (
        <div 
            className="h-9 w-full border border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-black overflow-hidden rounded flex"
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            <EditableField value={val} onChange={v => set((prev: any) => ({...prev, [field]: Number(v)}))} type={type} currency={curr} suffix={suffix} decimalPlaces={dp} />
        </div>
    );
    
    const Out = ({ val, type="number", curr, suffix, dp, tooltip }: any) => (
        <div 
            className="h-9 w-full border border-pe-border dark:border-slate-700 bg-pe-tile-header dark:bg-slate-800 text-pe-accent-text dark:text-[#E6C299] overflow-hidden rounded flex pointer-events-none"
            data-tooltip-id="app-tooltip" 
            data-tooltip-content={tooltip}
        >
            <EditableField value={val} onChange={() => {}} readOnly={true} type={type} currency={curr} suffix={suffix} decimalPlaces={dp} />
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-pe-card dark:bg-slate-900/30 border border-pe-border dark:border-slate-700/50 rounded-xl p-5 shadow-lg relative">
            <div className="flex items-center gap-2 mb-6 border-b border-pe-border dark:border-slate-700/50 pb-4">
                <div className="p-2 bg-pe-tile-header dark:bg-slate-800 rounded-lg text-pe-accent-text dark:text-[#E6C299]">
                    <IconCalculator />
                </div>
                <h3 className="text-xl font-bold text-pe-text dark:text-white">Lohn & Durchschnitt</h3>
            </div>
            
            <div className="space-y-8 flex-grow">
                <div>
                    <h4 className="text-pe-accent-text dark:text-[#E6C299] text-xs font-bold uppercase tracking-wider mb-4 border-l-2 border-[#E6C299] pl-2">
                        Stunden-Monatslohn (Faktor {Wochensatz.toFixed(2).replace('.', ',')})
                    </h4>
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <Group label="Stundenlohn">
                                <Inp val={sz1.stdLohn} set={setSz1} field="stdLohn" type="currency" curr={currency} dp={2} tooltip="Dein gewünschter Brutto-Stundenlohn." />
                            </Group>
                            <Group label="Wochenstd.">
                                <Inp val={sz1.woStd} set={setSz1} field="woStd" suffix="Std." dp={1} tooltip="Vertragliche Arbeitsstunden pro Woche." />
                            </Group>
                            <Group label="Brutto">
                                <Out val={sz1_brutto} type="currency" curr={currency} dp={2} tooltip="Errechnetes Monats-Gehalt (Brutto)." />
                            </Group>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Group label="Brutto">
                                <Inp val={sz2.brutto} set={setSz2} field="brutto" type="currency" curr={currency} dp={2} tooltip="Dein Ziel-Monatsgehalt (Brutto)." />
                            </Group>
                            <Group label="Wochenstd.">
                                <Inp val={sz2.woStd} set={setSz2} field="woStd" suffix="Std." dp={1} tooltip="Stunden pro Woche." />
                            </Group>
                            <Group label="Stundenlohn">
                                <Out val={sz2_stdLohn} type="currency" curr={currency} dp={2} tooltip="Erforderlicher Stundenlohn für dieses Ziel." />
                            </Group>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Group label="Brutto">
                                <Inp val={sz3.brutto} set={setSz3} field="brutto" type="currency" curr={currency} dp={2} tooltip="Dein Ziel-Monatsgehalt (Brutto)." />
                            </Group>
                            <Group label="Stundenlohn">
                                <Inp val={sz3.stdLohn} set={setSz3} field="stdLohn" type="currency" curr={currency} dp={2} tooltip="Dein aktuellen Stundenlohn." />
                            </Group>
                            <Group label="Wochenstd.">
                                <Out val={sz3_woStd} suffix="Std." dp={1} tooltip="So viel musst du arbeiten, um das Brutto zu erreichen." />
                            </Group>
                        </div>
                    </div>
                </div>

                <div className="pt-4 border-t border-pe-border dark:border-slate-700/50">
                    <h4 className="text-pe-accent-text dark:text-[#E6C299] text-xs font-bold uppercase tracking-wider mb-4 border-l-2 border-[#E6C299] pl-2">
                        Durchschnittsrechner
                    </h4>
                    <div className="grid grid-cols-3 gap-3">
                        <Group label="Std / Tag">
                            <Inp val={basis.stdTag} set={setBasis} field="stdTag" suffix="Std." dp={1} tooltip="Durchschnittliche Arbeitsstunden pro Anwesenheitstag." />
                        </Group>
                        <Group label="Tage / Monat">
                            <Inp val={basis.tageMonat} set={setBasis} field="tageMonat" suffix="Tage" dp={0} tooltip="Es gibt keine halben Tage! Es werden hier immer nur ganze Tage vermerkt, egal ob der Mitarbeiter nur einen halben Tag da ist." />
                        </Group>
                        <Group label="Ø Std / Woche">
                            <Out val={schnittWoche} suffix="Std." dp={1} tooltip="Errechneter Wochendurchschnitt." />
                        </Group>
                    </div>
                </div>
            </div>
        </div>
    );
};

type GehaltEingabeModus = 'brutto' | 'stundenlohn';

const GehaltModusToggle = ({
    mode,
    onChange,
    baseLabel = 'Brutto',
}: {
    mode: GehaltEingabeModus;
    onChange: (mode: GehaltEingabeModus) => void;
    baseLabel?: string;
}) => {
    const btn = (value: GehaltEingabeModus, label: string) => {
        const active = mode === value;
        return (
            <button
                type="button"
                onClick={() => onChange(value)}
                className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded border transition-all ${
                    active
                        ? 'bg-[#E6C299] text-black border-[#E6C299]'
                        : 'bg-transparent text-stone-500 dark:text-slate-500 border-pe-border dark:border-slate-700 hover:border-[#E6C299]/50'
                }`}
            >
                {label}
            </button>
        );
    };
    return (
        <div className="inline-flex items-center gap-1 mt-1.5" role="group" aria-label="Gehaltseingabe">
            {btn('brutto', baseLabel)}
            {btn('stundenlohn', 'Std.')}
        </div>
    );
};

const PersonalTable = ({
    title,
    dataKey,
    data,
    columns,
    newRowTemplate,
    currency,
    gehaltEingabeModus,
    onGehaltEingabeModusChange,
    itBzActive,
    atActive,
    onOpenItBzInfo,
}: any) => {
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const addTableRow = useAppStore((s) => s.addTableRow);
    const updateTableRow = useAppStore((s) => s.updateTableRow);
    const deleteTableRow = useAppStore((s) => s.deleteTableRow);

    // Aktive produktive Mitarbeiter aus clientData.personalData (Originalwerte für Dropdown & Überbuchungs-Schutz)
    const rawProductiveEmployees = useMemo(() => {
        const list = Array.isArray(clientData?.personalData) ? clientData.personalData : [];
        return list.filter((p: any) => p && p.id && typeof p.name === 'string' && p.name.trim() !== '');
    }, [clientData?.personalData]);

    const rawUnproductiveRows = useMemo(
        () => (Array.isArray(clientData?.unproductivePersonalData) ? clientData.unproductivePersonalData : []),
        [clientData?.unproductivePersonalData]
    );

    if (!settings?.country) return null;
    const wochensatz = Number(settings.global?.Wochensatz) || 4.33;
    const findRowIndexById = (id: string) => (data || []).findIndex((r: { id: string }) => r.id === id);

    /** Berechnet für eine unproduktive Split-Zeile das maximal abziehbare Gehalt / Wochenstunden des verknüpften MA */
    const getMaxSplitAllowance = (currentRowId: string, linkedProdId?: string) => {
        if (!linkedProdId) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        const prod = rawProductiveEmployees.find((p: any) => String(p.id) === String(linkedProdId));
        if (!prod) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        const baseWage = Math.max(
            0,
            itBzActive && Number(prod.netWage) > 0 ? Number(prod.netWage) : Number(prod.grundgehalt) || 0
        );
        const baseHours = Math.max(0, Number(prod.wochenstunden) || 0);

        let otherDeductedWage = 0;
        let otherDeductedHours = 0;
        for (const u of rawUnproductiveRows) {
            if (!u || u.id === currentRowId) continue;
            if (u.taetigkeit === 'Unprod. Anteil' && u.isSplitDeduction !== false && String(u.linkedProductiveEmployeeId) === String(linkedProdId)) {
                otherDeductedWage += Math.max(0, Number(u.grundgehalt) || 0);
                otherDeductedHours += Math.max(0, Number(u.wochenstunden) || 0);
            }
        }
        return {
            maxWage: Math.max(0, baseWage - otherDeductedWage),
            maxHours: Math.max(0, baseHours - otherDeductedHours),
            prodBaseWage: baseWage,
            prodBaseHours: baseHours,
        };
    };

    const monatsstundenOf = (wochenstunden: number) => (Number(wochenstunden) || 0) * wochensatz;

    /** Stundenlohn → immer Brutto (grundgehalt) bzw. Netto in busta (IT_BZ) speichern; Matrix bleibt unverändert. */
    const applyStundenlohnAsBrutto = (rowIndex: number, row: any, stundenlohn: number) => {
        const monatsstunden = monatsstundenOf(row.wochenstunden);
        let brutto = monatsstunden > 0 ? Number(stundenlohn) * monatsstunden : 0;
        if (dataKey === 'unproductivePersonalData' && row.taetigkeit === 'Unprod. Anteil' && row.linkedProductiveEmployeeId && row.isSplitDeduction !== false) {
            const { maxWage } = getMaxSplitAllowance(row.id, row.linkedProductiveEmployeeId);
            brutto = Math.min(Math.max(0, brutto), maxWage);
        }
        const storedBrutto =
            dataKey === 'personalData' && Number(row.splitDeductedGrundgehalt) > 0
                ? Math.max(0, brutto) + Number(row.splitDeductedGrundgehalt)
                : brutto;
        updateTableRow(dataKey, rowIndex, {
            grundgehalt: storedBrutto,
            ...(itBzActive ? { netWage: storedBrutto } : {}),
        });
    };

    const renderCell = (row: any, col: any, rowIndex: number) => {
        const update = (val: any) => updateTableRow(dataKey, rowIndex, col.key, val);
        const isUnprodAnteilRow =
            dataKey === 'unproductivePersonalData' && row.taetigkeit === 'Unprod. Anteil';
        // Fallback: falls der Name mit einem produktiven MA übereinstimmt, aber linkedProductiveEmployeeId noch nicht gesetzt war
        const matchedProdByName = isUnprodAnteilRow
            ? rawProductiveEmployees.find(
                  (p: any) =>
                      String(p.id) === String(row.linkedProductiveEmployeeId || '') ||
                      (row.name && String(p.name).trim() === String(row.name).trim())
              )
            : null;
        const effectiveLinkedProdId = row.linkedProductiveEmployeeId || matchedProdByName?.id;
        const hasLinkedProd = isUnprodAnteilRow && Boolean(effectiveLinkedProdId);
        const isSplitActive = hasLinkedProd && row.isSplitDeduction !== false;

        /**
         * Schaltet die Kopplung (Split-Abzug) ein oder aus, OHNE dass die aktuellen Zahlen oben/unten springen:
         * - Beim Ausschalten (nextSplit = false): Der aktuelle produktive Restwert oben wird fest in personalData
         *   übernommen, sodass oben (z.B. 550 €) und unten (z.B. 1.350 €) exakt stehen bleiben und ab sofort
         *   unabhängig voneinander frei erhöht/geändert werden können.
         * - Beim Einschalten (nextSplit = true): Oben + unten werden wieder zum gemeinsamen Gesamtbudget addiert,
         *   sodass die aktuellen Anzeigewerte oben und unten unverändert bleiben, aber weitere +/- Schritte wieder
         *   synchron gegenläufig verrechnet werden.
         */
        const handleToggleSplitCoupling = (nextSplit: boolean) => {
            if (!effectiveLinkedProdId) return;
            const prodList = Array.isArray(clientData?.personalData) ? clientData.personalData : [];
            const prodIdx = prodList.findIndex(
                (p: any) => String(p?.id) === String(effectiveLinkedProdId)
            );
            const rawProd = prodIdx >= 0 ? prodList[prodIdx] : null;
            const curUnprodWage = Math.max(0, Number(row.grundgehalt) || 0);
            const curUnprodHours = Math.max(0, Number(row.wochenstunden) || 0);

            if (rawProd && prodIdx >= 0) {
                const rawProdWage = Math.max(
                    0,
                    itBzActive && Number(rawProd.netWage) > 0
                        ? Number(rawProd.netWage)
                        : Number(rawProd.grundgehalt) || 0
                );
                const rawProdHours = Math.max(0, Number(rawProd.wochenstunden) || 0);

                if (!nextSplit && isSplitActive) {
                    // War gekoppelt -> jetzt entkoppeln: aktuellen Restwert oben festschreiben!
                    const frozenProdWage = Math.max(0, rawProdWage - curUnprodWage);
                    const frozenProdHours = Math.max(0, rawProdHours - curUnprodHours);
                    updateTableRow('personalData', prodIdx, {
                        grundgehalt: frozenProdWage,
                        ...(itBzActive ? { netWage: frozenProdWage } : {}),
                        wochenstunden: frozenProdHours,
                    });
                } else if (nextSplit && !isSplitActive) {
                    // War frei -> jetzt wieder koppeln: aktuelles Oben + Unten als Gesamtbudget setzen,
                    // damit beim Einschalten die angezeigten Werte oben & unten exakt gleich bleiben!
                    const combinedWage = rawProdWage + curUnprodWage;
                    const combinedHours = rawProdHours + curUnprodHours;
                    updateTableRow('personalData', prodIdx, {
                        grundgehalt: combinedWage,
                        ...(itBzActive ? { netWage: combinedWage } : {}),
                        wochenstunden: combinedHours,
                    });
                }
            }

            updateTableRow(dataKey, rowIndex, {
                ...(effectiveLinkedProdId
                    ? { linkedProductiveEmployeeId: effectiveLinkedProdId }
                    : {}),
                isSplitDeduction: nextSplit,
            });
        };

        /** Setzt das unproduktive Gehalt: wenn gekoppelt inkl. Abzug & Stunden-Sync, wenn frei (entkoppelt) unabhängig */
        const applySplitWageWithSyncedHours = (targetWage: number) => {
            if (!isSplitActive) {
                const freeWage = Math.max(0, Math.round(targetWage * 100) / 100);
                updateTableRow(dataKey, rowIndex, {
                    ...(effectiveLinkedProdId
                        ? { linkedProductiveEmployeeId: effectiveLinkedProdId }
                        : {}),
                    isSplitDeduction: false,
                    grundgehalt: freeWage,
                    ...(itBzActive ? { netWage: freeWage } : {}),
                });
                return;
            }
            const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getMaxSplitAllowance(
                row.id,
                effectiveLinkedProdId
            );
            const clampedWage = Math.round(Math.min(Math.max(0, targetWage), maxWage) * 100) / 100;
            const patch: Record<string, any> = {
                isSplitDeduction: true,
                grundgehalt: clampedWage,
                ...(itBzActive ? { netWage: clampedWage } : {}),
            };
            if (!row.linkedProductiveEmployeeId && effectiveLinkedProdId) {
                patch.linkedProductiveEmployeeId = effectiveLinkedProdId;
            }
            if (prodBaseWage > 0 && prodBaseHours > 0) {
                const syncedHours =
                    Math.round((prodBaseHours * (clampedWage / prodBaseWage)) * 100) / 100;
                patch.wochenstunden = Math.min(Math.max(0, syncedHours), maxHours);
            }
            updateTableRow(dataKey, rowIndex, patch);
        };

        /** Setzt die unproduktiven Wochenstunden: wenn gekoppelt inkl. Abzug & Gehalt-Sync, wenn frei unabhängig */
        const applySplitHoursWithSyncedWage = (targetHours: number) => {
            if (!isSplitActive) {
                const freeHours = Math.max(0, Math.round(targetHours * 100) / 100);
                updateTableRow(dataKey, rowIndex, {
                    ...(effectiveLinkedProdId
                        ? { linkedProductiveEmployeeId: effectiveLinkedProdId }
                        : {}),
                    isSplitDeduction: false,
                    wochenstunden: freeHours,
                });
                return;
            }
            const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getMaxSplitAllowance(
                row.id,
                effectiveLinkedProdId
            );
            const clampedHours = Math.round(Math.min(Math.max(0, targetHours), maxHours) * 100) / 100;
            const patch: Record<string, any> = {
                isSplitDeduction: true,
                wochenstunden: clampedHours,
            };
            if (!row.linkedProductiveEmployeeId && effectiveLinkedProdId) {
                patch.linkedProductiveEmployeeId = effectiveLinkedProdId;
            }
            if (prodBaseHours > 0 && prodBaseWage > 0) {
                const syncedWage =
                    Math.round((prodBaseWage * (clampedHours / prodBaseHours)) * 100) / 100;
                const finalWage = Math.min(Math.max(0, syncedWage), maxWage);
                patch.grundgehalt = finalWage;
                if (itBzActive) {
                    patch.netWage = finalWage;
                }
            }
            updateTableRow(dataKey, rowIndex, patch);
        };

        switch (col.component) {
            case 'text-input': {
                if (col.key === 'name' && isUnprodAnteilRow) {
                    return (
                        <div
                            className="w-full h-full relative bg-stone-100/60 dark:bg-black/40 group flex"
                            data-tooltip-id="app-tooltip"
                            data-tooltip-content="Produktiven Mitarbeiter auswählen, dessen unproduktiver Anteil erfasst wird."
                        >
                            <select
                                value={effectiveLinkedProdId || ''}
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    const prodMa = rawProductiveEmployees.find(
                                        (p: any) => String(p.id) === String(selectedId)
                                    );
                                    updateTableRow(dataKey, rowIndex, {
                                        linkedProductiveEmployeeId: selectedId || undefined,
                                        isSplitDeduction: Boolean(selectedId),
                                        name: prodMa?.name ? String(prodMa.name) : '',
                                    });
                                }}
                                className="w-full h-full bg-transparent text-pe-accent-text dark:text-[#E6C299] text-center text-sm font-bold appearance-none cursor-pointer focus:outline-none focus:bg-stone-100 dark:focus:bg-slate-900 px-2"
                            >
                                <option value="" className="bg-pe-card dark:bg-black text-stone-400">
                                    Mitarbeiter wählen...
                                </option>
                                {rawProductiveEmployees.map((p: any) => (
                                    <option
                                        key={p.id}
                                        value={p.id}
                                        className="bg-pe-card dark:bg-black text-pe-accent-text dark:text-[#E6C299]"
                                    >
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-pe-accent-text dark:text-[#E6C299] opacity-50 text-[10px]">
                                ▼
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="w-full h-full flex bg-stone-100/60 dark:bg-black/40 px-2" data-tooltip-id="app-tooltip" data-tooltip-content={col.tooltip}>
                        <EditableField value={row[col.key]||''} onChange={update} />
                    </div>
                );
            }
            case 'dropdown': 
                return (
                    <div className="w-full h-full px-1">
                        <DropdownCell value={row[col.key]} options={col.options} onChange={e => update(e.target.value)} tooltip={col.tooltip} />
                    </div>
                );
            case 'mindestlohn': 
                return <MindestlohnIndicator stundenlohn={row.stundenlohnGrund} mindestlohn={settings.country.Mindestlohn} taetigkeit={row.taetigkeit} countryId={settings.selectedCountryId} tooltip={col.tooltip} brutto={row.bruttolohnGesamt} />;
            case 'calculated': 
                return <ResultCell value={row[col.key]} formula={col.formulaName} type={col.type} currency={currency} suffix={col.suffix} decimalPlaces={col.decimalPlaces} tooltip={col.tooltip} />;
            case 'gehalt-auswertung': {
                const isStd = gehaltEingabeModus === 'stundenlohn';
                // Umschalter: Eingabe Std. → Auswertung zeigt Grundbrutto; Eingabe Brutto → Std.Lohn Grund
                const value = isStd ? (row.grundgehalt ?? 0) : (row.stundenlohnGrund ?? 0);
                const tooltip = isStd
                    ? 'Errechnetes Grund-Bruttogehalt (Monatsbasis) aus Stundenlohn × Wochenstunden × Wochensatz.'
                    : (col.tooltip || 'Stundenlohn nur auf Basis des Grundgehalts.');
                return (
                    <ResultCell
                        value={value}
                        type="currency"
                        currency={currency}
                        decimalPlaces={2}
                        tooltip={tooltip}
                    />
                );
            }
            case 'checkbox':
                return (
                    <div className="w-full h-full flex items-center justify-center bg-stone-100/60 dark:bg-black/40" data-tooltip-id="app-tooltip" data-tooltip-content={col.tooltip}>
                         <input 
                            type="checkbox" 
                            checked={!!row[col.key]} 
                            onChange={(e) => update(e.target.checked)}
                            className="h-5 w-5 rounded border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-slate-800 text-blue-600 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                        />
                    </div>
                );
            case 'foerderung': {
                const aktiv = row.isGefoerdert === true;
                const art = row.foerderArt === 'euro' ? 'euro' : 'prozent';
                const quote = Number(row.foerderquoteProzent) > 0 ? Number(row.foerderquoteProzent) : 50;
                const euro = Number(row.foerderEuro) > 0 ? Number(row.foerderEuro) : 0;
                const brutto = Number(row.grundgehalt) || 0;
                const zuschuss = art === 'euro'
                    ? (brutto > 0 ? Math.min(euro, brutto) : euro)
                    : brutto * (Math.min(100, quote) / 100);
                const stepQuote = (dir: number) => {
                    const next = Math.min(100, Math.max(0, quote + dir * 10));
                    updateTableRow(dataKey, rowIndex, {
                        isGefoerdert: true,
                        foerderArt: 'prozent',
                        foerderquoteProzent: next,
                    });
                };
                const fieldCls = 'w-14 text-center text-xs font-mono bg-transparent border border-pe-border rounded py-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
                const stepBtn = 'w-6 h-6 rounded border border-pe-border text-pe-text text-sm leading-none hover:border-[#E6C299]';
                return (
                    <div
                        className="w-full h-full flex flex-col items-center justify-center gap-1 bg-stone-100/60 dark:bg-black/40 px-2 py-1"
                        data-tooltip-id="app-tooltip"
                        data-tooltip-content="Selten. Bruttolohn bleibt ungekürzt. Prozent in 10er-Schritten oder fester Euro-Betrag. Abzug erst am Ende vom Gesamtkosten-Soll."
                    >
                        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-text cursor-pointer">
                            <input
                                type="checkbox"
                                checked={aktiv}
                                onChange={(e) =>
                                    updateTableRow(dataKey, rowIndex, {
                                        isGefoerdert: e.target.checked,
                                        foerderArt: art,
                                        foerderquoteProzent: quote,
                                    })
                                }
                                className="h-4 w-4 rounded border-pe-border-strong bg-pe-input text-emerald-600"
                            />
                            Förderung
                        </label>
                        {aktiv && (
                            <>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => updateTableRow(dataKey, rowIndex, { isGefoerdert: true, foerderArt: 'prozent', foerderquoteProzent: quote })}
                                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${art === 'prozent' ? 'bg-[#E6C299] text-black' : 'border border-pe-border text-pe-muted'}`}
                                    >
                                        %
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateTableRow(dataKey, rowIndex, { isGefoerdert: true, foerderArt: 'euro' })}
                                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${art === 'euro' ? 'bg-[#E6C299] text-black' : 'border border-pe-border text-pe-muted'}`}
                                    >
                                        €
                                    </button>
                                </div>
                                {art === 'prozent' ? (
                                    <div className="flex items-center gap-1">
                                        <button type="button" className={stepBtn} onClick={() => stepQuote(-1)} aria-label="10 Prozent weniger">−</button>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={String(quote).replace('.', ',')}
                                            onChange={(e) => {
                                                const n = Number(String(e.target.value).replace(',', '.'));
                                                updateTableRow(dataKey, rowIndex, {
                                                    isGefoerdert: true,
                                                    foerderArt: 'prozent',
                                                    foerderquoteProzent: Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0,
                                                });
                                            }}
                                            className={fieldCls}
                                        />
                                        <button type="button" className={stepBtn} onClick={() => stepQuote(1)} aria-label="10 Prozent mehr">+</button>
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={euro ? String(euro).replace('.', ',') : ''}
                                        placeholder="Betrag"
                                        onChange={(e) => {
                                            const n = Number(String(e.target.value).replace(',', '.'));
                                            updateTableRow(dataKey, rowIndex, {
                                                isGefoerdert: true,
                                                foerderArt: 'euro',
                                                foerderEuro: Number.isFinite(n) ? Math.max(0, n) : 0,
                                            });
                                        }}
                                        className={`${fieldCls} w-20`}
                                    />
                                )}
                                <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400">
                                    −{zuschuss.toFixed(0)} € neutral
                                </span>
                            </>
                        )}
                    </div>
                );
            }
            case 'gehalt-eingabe': {
                const isStd = gehaltEingabeModus === 'stundenlohn';
                const baseMonthlyVal = itBzActive
                    ? (Number(row.netWage) > 0 ? Number(row.netWage) : Number(row.grundgehalt) || 0)
                    : (Number(row.grundgehalt) || 0);
                const displayValue = isStd ? (row.stundenlohnGrund ?? 0) : baseMonthlyVal;
                const prodDeductedWage =
                    dataKey === 'personalData' ? Number(row.splitDeductedGrundgehalt) || 0 : 0;
                const tooltip = isStd
                    ? (itBzActive
                        ? 'Netto-Stundenlohn eingeben — Monats-Netto ("Netto in busta") wird aus Wochenstunden × Wochensatz berechnet und gespeichert.'
                        : 'Stundenlohn eingeben — Brutto-Monatsgehalt wird aus Wochenstunden × Wochensatz berechnet und gespeichert.')
                    : (col.tooltip || (itBzActive ? 'Vereinbartes Auszahlungs-Netto ("Netto in busta").' : 'Vertragliches Brutto-Monatsgehalt (fix).'));
                return (
                    <div
                        className="w-full h-full bg-stone-100/60 dark:bg-black/40 flex items-center px-1.5 relative gap-1"
                        data-tooltip-id="app-tooltip"
                        data-tooltip-content={
                            hasLinkedProd
                                ? 'Unproduktiver Gehaltsanteil: Mit + / − in 100-€-Schritten von oben abziehen (Stunden passen sich automatisch an, damit der Stundenlohn gleich bleibt) oder Betrag direkt eingeben.'
                                : prodDeductedWage > 0
                                    ? `Produktiver Restlohn nach Abzug von ${prodDeductedWage.toFixed(2).replace('.', ',')} € unprod. Anteil (Gesamtvertrag: ${(Number(row.rawGrundgehalt) || 0).toFixed(2).replace('.', ',')} €).`
                                    : tooltip
                        }
                    >
                        {hasLinkedProd && (
                            <div className="inline-flex shrink-0 items-center rounded border border-pe-border-strong dark:border-slate-700 overflow-hidden bg-pe-card dark:bg-slate-900">
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleSplitCoupling(!isSplitActive)}
                                    title={
                                        isSplitActive
                                            ? 'Kopplung AKTIV (Abzug von oben). Klicken zum Entkoppeln — aktuelle Werte oben & unten bleiben stehen und sind danach frei änderbar!'
                                            : 'Kopplung AUS (Frei). Werte oben & unten sind unabhängig. Klicken, um wieder mit oben zu koppeln.'
                                    }
                                    className={`px-1.5 py-1 text-[9px] font-black uppercase leading-none border-r border-pe-border-strong dark:border-slate-700 transition-all ${
                                        isSplitActive
                                            ? 'bg-[#E6C299] text-black'
                                            : 'bg-slate-800 text-slate-400 hover:text-white'
                                    }`}
                                >
                                    {isSplitActive ? 'Abzug' : 'Frei'}
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(baseMonthlyVal - 100)}
                                    title={
                                        isSplitActive
                                            ? '−100 € unproduktiver Anteil (Stunden & oberes Gehalt passen sich synchron an)'
                                            : '−100 € (frei, ohne Abzug oben)'
                                    }
                                    className="px-1.5 py-1 text-xs font-black leading-none text-stone-600 dark:text-slate-300 hover:bg-rose-500/20 hover:text-rose-400 active:scale-95 transition-all"
                                >
                                    −
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(baseMonthlyVal + 100)}
                                    title={
                                        isSplitActive
                                            ? '+100 € von oben in unproduktiven Anteil übertragen (Stunden passen sich synchron an)'
                                            : '+100 € (frei, ohne Abzug oben)'
                                    }
                                    className="px-1.5 py-1 text-xs font-black leading-none border-l border-pe-border-strong dark:border-slate-700 text-pe-accent-text dark:text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400 active:scale-95 transition-all"
                                >
                                    +
                                </button>
                            </div>
                        )}
                        <div className={`flex-1 h-full flex ${hasLinkedProd ? 'min-w-[5.5rem]' : 'min-w-0'}`}>
                            <EditableField
                                value={displayValue}
                                onChange={(val: any) => {
                                    let numVal = Math.max(0, Number(val) || 0);
                                    if (isStd) {
                                        applyStundenlohnAsBrutto(rowIndex, row, numVal);
                                    } else if (hasLinkedProd && isSplitActive) {
                                        // Direkte Zahleneingabe: Nur das Gehalt setzen (Stunden bleiben frei wählbar);
                                        // nur falls Stunden noch 0 sind, einmalig die proportionalen Stunden vorschlagen.
                                        const { maxWage, maxHours, prodBaseWage, prodBaseHours } =
                                            getMaxSplitAllowance(row.id, effectiveLinkedProdId);
                                        numVal = Math.round(Math.min(numVal, maxWage) * 100) / 100;
                                        const curHours = Number(row.wochenstunden) || 0;
                                        const patch: Record<string, any> = {
                                            ...(effectiveLinkedProdId
                                                ? { linkedProductiveEmployeeId: effectiveLinkedProdId }
                                                : {}),
                                            isSplitDeduction: true,
                                            grundgehalt: numVal,
                                            ...(itBzActive ? { netWage: numVal } : {}),
                                        };
                                        if (curHours === 0 && numVal > 0 && prodBaseWage > 0 && prodBaseHours > 0) {
                                            const suggestedHours =
                                                Math.round((prodBaseHours * (numVal / prodBaseWage)) * 100) / 100;
                                            patch.wochenstunden = Math.min(Math.max(0, suggestedHours), maxHours);
                                        }
                                        updateTableRow(dataKey, rowIndex, patch);
                                    } else {
                                        const storedVal =
                                            dataKey === 'personalData' && prodDeductedWage > 0
                                                ? numVal + prodDeductedWage
                                                : numVal;
                                        updateTableRow(dataKey, rowIndex, {
                                            grundgehalt: storedVal,
                                            ...(itBzActive ? { netWage: storedVal } : {}),
                                        });
                                    }
                                }}
                                type="currency"
                                currency={currency}
                                decimalPlaces={2}
                            />
                        </div>
                        {prodDeductedWage > 0 && (
                            <span className="absolute bottom-0 right-1 text-[8px] font-bold text-amber-500/90 pointer-events-none">
                                −{Math.round(prodDeductedWage)} € Split
                            </span>
                        )}
                    </div>
                );
            }
            default: {
                if (col.key === 'wochenstunden') {
                    const prodDeductedHours =
                        dataKey === 'personalData' ? Number(row.splitDeductedWochenstunden) || 0 : 0;
                    const currentHoursVal = Number(row.wochenstunden) || 0;
                    return (
                        <div
                            className="w-full h-full bg-stone-100/60 dark:bg-black/40 flex items-center px-1.5 relative gap-1"
                            data-tooltip-id="app-tooltip"
                            data-tooltip-content={
                                hasLinkedProd
                                    ? 'Unproduktive Wochenstunden: Mit + / − stundenweise anpassen (Gehalt passt sich proportional an) oder Stunden direkt eingeben.'
                                    : prodDeductedHours > 0
                                        ? `Produktive Reststunden nach Abzug von ${prodDeductedHours.toFixed(1).replace('.', ',')} Std. unprod. Anteil (Gesamtvertrag: ${(Number(row.rawWochenstunden) || 0).toFixed(1).replace('.', ',')} Std.).`
                                        : col.tooltip
                            }
                        >
                            {hasLinkedProd && (
                                <div className="inline-flex shrink-0 items-center rounded border border-pe-border-strong dark:border-slate-700 overflow-hidden bg-pe-card dark:bg-slate-900">
                                    <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => applySplitHoursWithSyncedWage(currentHoursVal - 1)}
                                        title={
                                            isSplitActive
                                                ? '−1 Std. unproduktiver Anteil (Gehalt & obere Stunden passen sich synchron an)'
                                                : '−1 Std. (frei, ohne Abzug oben)'
                                        }
                                        className="px-1.5 py-1 text-xs font-black leading-none text-stone-600 dark:text-slate-300 hover:bg-rose-500/20 hover:text-rose-400 active:scale-95 transition-all"
                                    >
                                        −
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => applySplitHoursWithSyncedWage(currentHoursVal + 1)}
                                        title={
                                            isSplitActive
                                                ? '+1 Std. von oben in unproduktiven Anteil übertragen (Gehalt passt sich synchron an)'
                                                : '+1 Std. (frei, ohne Abzug oben)'
                                        }
                                        className="px-1.5 py-1 text-xs font-black leading-none border-l border-pe-border-strong dark:border-slate-700 text-pe-accent-text dark:text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400 active:scale-95 transition-all"
                                    >
                                        +
                                    </button>
                                </div>
                            )}
                            <div className={`flex-1 h-full flex ${hasLinkedProd ? 'min-w-[4.5rem]' : 'min-w-0'}`}>
                                <EditableField
                                    value={row[col.key]}
                                    onChange={(val: any) => {
                                        let newHours = Math.max(0, Number(val) || 0);
                                        if (hasLinkedProd && isSplitActive) {
                                            const { maxHours } = getMaxSplitAllowance(
                                                row.id,
                                                effectiveLinkedProdId
                                            );
                                            newHours = Math.min(newHours, maxHours);
                                            updateTableRow(dataKey, rowIndex, {
                                                ...(effectiveLinkedProdId ? { linkedProductiveEmployeeId: effectiveLinkedProdId } : {}),
                                                isSplitDeduction: true,
                                                wochenstunden: newHours,
                                            });
                                            return;
                                        }
                                        const storedHours =
                                            dataKey === 'personalData' && prodDeductedHours > 0
                                                ? newHours + prodDeductedHours
                                                : newHours;

                                        if (gehaltEingabeModus === 'stundenlohn') {
                                            const oldMonats = monatsstundenOf(row.wochenstunden);
                                            const hourly =
                                                oldMonats > 0
                                                    ? (Number(row.grundgehalt) || 0) / oldMonats
                                                    : Number(row.stundenlohnGrund) || 0;
                                            const newBrutto = hourly * monatsstundenOf(newHours);
                                            const prodDeductedWage =
                                                dataKey === 'personalData'
                                                    ? Number(row.splitDeductedGrundgehalt) || 0
                                                    : 0;
                                            const storedBrutto =
                                                dataKey === 'personalData' && prodDeductedWage > 0
                                                    ? newBrutto + prodDeductedWage
                                                    : newBrutto;
                                            updateTableRow(dataKey, rowIndex, {
                                                wochenstunden: storedHours,
                                                grundgehalt: storedBrutto,
                                            });
                                        } else {
                                            updateTableRow(dataKey, rowIndex, 'wochenstunden', storedHours);
                                        }
                                    }}
                                    type={col.type}
                                    currency={currency}
                                    suffix={col.suffix}
                                    decimalPlaces={col.decimalPlaces}
                                />
                            </div>
                            {prodDeductedHours > 0 && (
                                <span className="absolute bottom-0 right-1 text-[8px] font-bold text-amber-500/90 pointer-events-none">
                                    −{prodDeductedHours.toFixed(1).replace('.', ',')}h Split
                                </span>
                            )}
                        </div>
                    );
                }
                return (
                    <div 
                        className="w-full h-full bg-stone-100/60 dark:bg-black/40 flex px-2"
                        data-tooltip-id="app-tooltip" 
                        data-tooltip-content={col.tooltip}
                    >
                        <EditableField value={row[col.key]} onChange={update} type={col.type} currency={currency} suffix={col.suffix} decimalPlaces={col.decimalPlaces} />
                    </div>
                );
            }
        }
    };
    
    return (
        <Section className="mb-8 overflow-hidden bg-transparent border-none shadow-none">
            <div className="flex justify-between items-center mb-4 border-b border-pe-border dark:border-slate-700/50 pb-2">
                <SectionTitle>{title}</SectionTitle>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full border-separate border-spacing-y-4">
                    <thead>
                        <tr className="text-left">
                            <th className="px-4 py-2 w-8 text-center text-stone-500 dark:text-slate-500 font-normal uppercase text-[10px]">#</th>
                            {columns.map((col: any) => (
                                <th key={col.key} className="px-4 py-3 align-bottom" style={{ minWidth: col.minWidth || 'auto' }}>
                                    <span className="text-[10px] font-bold text-stone-500 dark:text-slate-500 uppercase tracking-widest leading-tight block">
                                        {col.key === 'grundgehalt' ? (
                                            gehaltEingabeModus === 'stundenlohn' ? (
                                                <>Std.<br />Lohn</>
                                            ) : itBzActive ? (
                                                <>Netto in<br />busta</>
                                            ) : atActive ? (
                                                <>Monatl. Bruttolohn<br />(Kollektivvertrag)</>
                                            ) : (
                                                <>Grund-<br />gehalt</>
                                            )
                                        ) : col.key === 'stundenlohnGrund' ? (
                                            gehaltEingabeModus === 'stundenlohn' ? (
                                                itBzActive ? <>Netto<br />Basis</> : <>Grund-<br />brutto</>
                                            ) : (
                                                <>Std.Lohn<br />Grund</>
                                            )
                                        ) : (
                                            col.label.split('\n').map((line: string, i: number) => (
                                                <React.Fragment key={i}>{line}{i < col.label.split('\n').length - 1 && <br />}</React.Fragment>
                                            ))
                                        )}
                                    </span>
                                    {col.key === 'grundgehalt' && onGehaltEingabeModusChange && (
                                        <div className="flex items-center gap-1.5">
                                            <GehaltModusToggle
                                                mode={gehaltEingabeModus}
                                                onChange={onGehaltEingabeModusChange}
                                                baseLabel={itBzActive ? 'Netto' : 'Brutto'}
                                            />
                                            {itBzActive && onOpenItBzInfo && (
                                                <button
                                                    type="button"
                                                    onClick={onOpenItBzInfo}
                                                    title="Lohnberechnung & Lohnfaktor Südtirol (IT_BZ) — Systematik & Nachweis"
                                                    className="mt-1.5 w-5 h-5 rounded-full border border-[#dcb68a]/60 bg-[#dcb68a]/15 text-[#dcb68a] hover:bg-[#dcb68a] hover:text-black flex items-center justify-center text-[10px] font-black transition-all"
                                                >
                                                    i
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </th>
                            ))}
                            <th className="p-2 w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data || []).map((row: any, i: number) => (
                            <tr key={row.id || i} className="group h-12 transition-all">
                                <td className="text-center text-stone-500 dark:text-slate-500 text-xs font-mono bg-pe-tile-header dark:bg-[#0f1115] rounded-l-lg border-l border-y border-pe-border dark:border-slate-800/50">{i+1}</td>
                                {columns.map((col: any, idx: number) => (
                                    <td key={`${row.id}-${col.key}`} className="p-0 h-12 bg-pe-card dark:bg-[#15171e] border-y border-pe-border dark:border-slate-800/50 relative">
                                        {renderCell(row, col, findRowIndexById(row.id))}
                                    </td>
                                ))}
                                <td className="text-center p-0 bg-pe-card dark:bg-[#15171e] rounded-r-lg border-r border-y border-pe-border dark:border-slate-800/50">
                                    <button onClick={() => deleteTableRow(dataKey, findRowIndexById(row.id))} className="text-stone-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-100/60 dark:hover:bg-red-900/10 transition-colors w-full h-full flex items-center justify-center font-bold" title="Löschen">×</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {(!data || data.length === 0) && <div className="p-10 text-center text-stone-400 dark:text-slate-600 italic text-sm bg-stone-100/50 dark:bg-[#15171e]/50 rounded-xl border border-dashed border-pe-border dark:border-slate-800">Keine Einträge vorhanden.</div>}
                
                <div className="mt-4 flex justify-start pl-1">
                    <button 
                        onClick={() => addTableRow(dataKey, newRowTemplate)} 
                        className="text-[10px] bg-[#E6C299] text-black px-8 py-2.5 rounded-lg hover:bg-[#d4b188] transition-all font-black uppercase tracking-tighter shadow-xl"
                    >
                        + Zeile hinzufügen
                    </button>
                </div>
            </div>
        </Section>
    );
};

// --- MAIN PAGE ---

const PersonalDatenPage = () => {
    const settings = useAppStore((state) => state.settings);
    const syncNavigation = useAppStore((state) => state.syncNavigation);
    const deleteTableRow = useAppStore((state) => state.deleteTableRow);
    const addTableRow = useAppStore((state) => state.addTableRow);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();
    const { isTeamMode } = useTeamBonus();
    const [teamBonusModalOpen, setTeamBonusModalOpen] = useState(false);
    const [itBzInfoModalOpen, setItBzInfoModalOpen] = useState(false);
    const [gehaltEingabeModus, setGehaltEingabeModus] = useState<GehaltEingabeModus>('brutto');
    const currency = useMemo(() => settings?.country?.currency || 'EUR', [settings]);
    const currencySymbol = currency === 'CHF' ? 'CHF' : '€';
    const itBzActive = useMemo(() => isItBzMode(settings?.selectedCountryId, settings), [settings]);
    const atActive = useMemo(() => isAtMode(undefined, { selectedCountryId: settings?.selectedCountryId }), [settings]);

    const newProd = useMemo(() => ({ name: "", taetigkeit: "Bitte auswählen", zugehoerigkeit: "Bitte auswählen", bonus: 0, grundgehalt: 0, lohnkostenzusatz: 0, wochenstunden: 0, arbeitstage: 0, info: "", arbeitetMitGeraeten: false }), []);
    const newUnprod = useMemo(() => ({ name: "", taetigkeit: "Bitte auswählen", grundgehalt: 0, lohnkostenzusatz: 0, bonus: 0, wochenstunden: 0, info: "" }), []);
    const newAzubi = useMemo(() => ({ name: "", taetigkeit: "Bitte auswählen", grundgehalt: 0, lohnkostenzusatz: 0, bonus: 0, wochenstunden: 0, info: "" }), []);

    // 💉 CHIRURGIE FIX: Automatische Initialisierung von leeren Tabellen
    useEffect(() => {
        // Verzögern, damit das Store-Binding sauber abgeschlossen ist
        const timer = setTimeout(() => {
            if (!personalData || personalData.length === 0) addTableRow('personalData', newProd);
            if (!unproductivePersonalData || unproductivePersonalData.length === 0) addTableRow('unproductivePersonalData', newUnprod);
            if (!azubiAssistenzData || azubiAssistenzData.length === 0) addTableRow('azubiAssistenzData', newAzubi);
        }, 300);
        return () => clearTimeout(timer);
    }, [personalData, unproductivePersonalData, azubiAssistenzData, addTableRow, newProd, newUnprod, newAzubi]);

    if (!settings) return <div className="w-full p-20 text-center text-stone-500 dark:text-slate-500 animate-pulse">Lade...</div>;
    
    const taetigkeitenProduktiv = ['Bitte auswählen', 'Friseur', 'Friseur (Führung)', 'Kosmetischer MA', 'Prod. Azubi', 'Sonstiges'];
    const taetigkeitenUnproduktiv = ['Bitte auswählen', 'Reinigung', 'Rezeption', 'Sozial Media', 'Hausmeister', 'Office', 'Unprod. Anteil', 'Sonstiges'];
    const taetigkeitenAzubi = ['Bitte auswählen', 'Azubi', 'Assistenz'];
    const zugehoerigkeitOptions = ['Bitte auswählen', 'weniger als 6 Monate', 'mehr als 6 Monate'];

    const tooltips = {
        name: "Der interne Name des Mitarbeiters.", 
        taetigkeit: "Rolle bestimmt Kennzahlen. 'Prod. Azubi' = Azubi mit Umsatz.", 
        zugehoerigkeit: "Wichtig: Probezeit (< 6 Monate) oder fest angestellt?", 
        grundgehalt: itBzActive
            ? "Vereinbartes Auszahlungs-Netto ('Netto in busta') — Basis für den Südtiroler Netto-Multiplikator."
            : atActive
                ? "Monatlicher Bruttolohn (Kollektivvertrag) — Basis für die Berechnung inkl. 13./14. Monatsgehalt & Dienstgeberabgaben."
                : "Brutto-Monatsgehalt (fix) — oder Umschalter auf Stundenlohn: Brutto wird aus Wochenstunden × Wochensatz berechnet und gespeichert.",
        lohnzusatz: "Steuerfreie Zuschläge, Fahrtkosten, VWL etc.", 
        bonus: "Bonus/Provision — nur manuell eintragen (z. B. nach Halbjahres-Entscheidung). Nie automatisch aus dem Team-Bonus-Modal.", 
        bruttolohnGesamt: itBzActive
            ? "Effektive Netto-Basis: Netto in busta + Zusatz + Bonus."
            : "Summe: Grundgehalt + Zusatz + Bonus.",
        wochenstunden: "Vertragliche Wochenarbeitszeit (bei IT_BZ: < 30h Teilzeit 1,65 / >= 30h Vollzeit 1,80).", 
        arbeitstage: "Es gibt keine halben Tage! Es werden hier immer nur ganze Tage vermerkt, egal ob der Mitarbeiter nur einen halben Tag da ist.", 
        stundenlohn: "Effektiver Stundenlohn (inkl. allem).",
        mindestlohn: "Rechtlicher Check auf Basis des Grundgehalts.", 
        sv: itBzActive
            ? "Südtiroler Aufschlag-Faktor (Azubi +42%, Teilzeit <30h +65%, Vollzeit >=30h +80% bzw. gem. StB-Override)."
            : atActive
                ? "Österreichischer Aufschlag inkl. 13./14. Monatsgehalt (UZ/WR) & Dienstgeberabgaben (Fachkraft +52,0%, Lehrling +35,0%)."
                : "Arbeitgeberanteil Sozialversicherung (ca. 21%).", 
        costoOverride: "Optionaler Steuerberater-Override (Costo Totale Aziendale lt. Lohnkostenspiegel). Wenn > 0, überschreibt dieser Wert den Standard-Stufenmultiplikator.",
        costoGesamt: atActive
            ? "Effektive Arbeitgeber-Gesamtkosten im Monat (inkl. 13./14. Monatsgehalt & DG-Abgaben)."
            : "Effektive Arbeitgeber-Gesamtkosten (Costo Totale Aziendale) im Monat.",
        info: "Notizen.",
        geraete: "Freischaltung des Geräte-Bereichs auf der Leistungsseite dieses Mitarbeiters."
    };

    const showGeraeteColumn = settings.global?.geraeteListe && settings.global.geraeteListe.length > 0;

    const baseCols = [
        { label: "Name", key: "name", component: "text-input", minWidth: "140px", tooltip: tooltips.name },
        { label: "Tätigkeit", key: "taetigkeit", component: "dropdown", minWidth: "150px", tooltip: tooltips.taetigkeit },
        { label: itBzActive ? "Netto in\nbusta" : atActive ? "Monatl. Bruttolohn\n(Kollektivvertrag)" : "Grund-\ngehalt", key: "grundgehalt", component: "gehalt-eingabe", type: "currency", minWidth: "180px", tooltip: tooltips.grundgehalt, decimalPlaces: 2 },
        { label: "Lohnkosten-\nzusatz", key: "lohnkostenzusatz", type: "currency", minWidth: "130px", tooltip: tooltips.lohnzusatz, decimalPlaces: 0 },
        { label: "Bonus", key: "bonus", type: "currency", minWidth: "100px", tooltip: tooltips.bonus, decimalPlaces: 0 },
        { label: itBzActive ? "Netto\nGesamt" : "Brutto\nØ", key: "bruttolohnGesamt", component: "calculated", type: "currency", minWidth: "150px", tooltip: tooltips.bruttolohnGesamt, decimalPlaces: 2 },
        ...(itBzActive ? [
            { label: "StB Costo\nOverride", key: "costoAziendaleOverride", type: "currency", minWidth: "135px", tooltip: tooltips.costoOverride, decimalPlaces: 2 },
            { label: "Costo\nAziendale", key: "agGesamt", component: "calculated", type: "currency", minWidth: "145px", tooltip: tooltips.costoGesamt, decimalPlaces: 2 },
        ] : atActive ? [
            { label: "AG-Gesamt\n(inkl. 13./14.)", key: "agGesamt", component: "calculated", type: "currency", minWidth: "145px", tooltip: tooltips.costoGesamt, decimalPlaces: 2 },
        ] : []),
        { label: "Wo-Std.", key: "wochenstunden", type: "number", minWidth: "140px", suffix: "Std.", tooltip: tooltips.wochenstunden, decimalPlaces: 1 },
        { label: "Tage/W", key: "arbeitstage", type: "number", minWidth: "80px", suffix: "Tage", tooltip: tooltips.arbeitstage, decimalPlaces: 0 },
        { label: "Std.Lohn\nGrund", key: "stundenlohnGrund", component: "gehalt-auswertung", type: "currency", minWidth: "120px", tooltip: "Stundenlohn nur auf Basis des Grundgehalts.", decimalPlaces: 2 },
        { label: "Std.Lohn\nGesamt", key: "stundenlohnGesamt", component: "calculated", type: "currency", minWidth: "120px", tooltip: tooltips.stundenlohn, decimalPlaces: 2 },
        { label: "Mindestl.", key: "mindestlohn", component: "mindestlohn", minWidth: "80px", tooltip: tooltips.mindestlohn },
        { label: itBzActive ? "Aufschlag\nIT_BZ" : atActive ? "Aufschlag\nAT (KV)" : "SV", key: "lohnnebenkostenSatz", component: "calculated", type: "percentage", minWidth: "100px", tooltip: tooltips.sv, decimalPlaces: 1 },
        { label: "Förderung", key: "isGefoerdert", component: "foerderung", minWidth: "168px", tooltip: "Ganz hinten, weil selten. Prozent in 10er-Schritten oder fester Euro-Betrag. Bruttolohn bleibt ungekürzt." },
        { label: "Info", key: "info", component: "text-input", minWidth: "400px", tooltip: tooltips.info }
    ];
    
    const prodColsDefinition = [
        baseCols[0], // Name
        { ...baseCols[1], options: taetigkeitenProduktiv }, // Tätigkeit
        { label: "Status", key: "zugehoerigkeit", component: "dropdown", options: zugehoerigkeitOptions, minWidth: "220px", tooltip: tooltips.zugehoerigkeit },
        ...(showGeraeteColumn ? [{ label: "Geräte?", key: "arbeitetMitGeraeten", component: "checkbox", minWidth: "70px", tooltip: tooltips.geraete }] : []),
        ...baseCols.slice(2) // Rest
    ];

    const colsUnprod = baseCols
        .filter(c => c.key !== 'arbeitstage')
        .map(c => {
            if (c.key === 'taetigkeit') return { ...c, options: taetigkeitenUnproduktiv };
            // Platz für Abzug/Frei + −/+ bzw. Stundenwert + Std.-Suffix
            if (c.key === 'grundgehalt') return { ...c, minWidth: '260px' };
            if (c.key === 'wochenstunden') return { ...c, minWidth: '200px' };
            return c;
        });
    const colsAzubi = baseCols
        .filter(c => c.key !== 'arbeitstage')
        .map(c => c.key === 'taetigkeit' ? {...c, options: taetigkeitenAzubi} : c);

    const handleSaveAndSync = () => {
        const clean = (list: any[], key: string) => { for (let i = list.length - 1; i >= 0; i--) { if (!list[i].name || list[i].name.trim() === '') deleteTableRow(key, i); } };
        clean(personalData, 'personalData'); clean(unproductivePersonalData, 'unproductivePersonalData'); clean(azubiAssistenzData, 'azubiAssistenzData');
        syncNavigation();
    };

    return (
        <div className="w-full">
            <div className="w-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch mb-8">
                <Section className="h-full bg-transparent border-none p-0 shadow-none">
                    <LeistungslohnProvisionen onOpenTeamBonusModal={() => setTeamBonusModalOpen(true)} />
                </Section>
                <Section className="h-full bg-transparent border-none p-0 shadow-none"><KalkulationsCockpit currency={currency} /></Section>
            </div>

            {isTeamMode && (
                <div className="w-full max-w-[1920px] mx-auto px-4 mb-6">
                    <div className="rounded-xl border border-amber-500/35 bg-amber-50 dark:bg-amber-950/25 px-5 py-4 shadow-sm">
                        <p className="text-sm text-amber-900 dark:text-amber-100 font-medium leading-relaxed">
                            <span className="font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 block mb-1">Team-Bonus aktiv</span>
                            PE-TeamBonus zeigt nur die laufende Vorschau/Sammlung. Personal-Bonus-Felder bleiben unberührt und werden erst nach dem Halbjahr manuell eingetragen, wenn wirklich ausgezahlt (oder anders vergütet) wird. Kein Automatismus.
                        </p>
                    </div>
                </div>
            )}
            
            <div className="w-full max-w-[1920px] mx-auto px-4 mb-4 flex items-center justify-between flex-wrap gap-3">
                <div className="inline-flex items-center gap-2.5 rounded-xl border border-[#dcb68a]/35 bg-[#15171e] px-4 py-2.5 shadow-sm">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Aktives Lohnmodell:
                    </span>
                    <span className="text-xs font-bold text-[#dcb68a]">
                        {itBzActive
                            ? 'Südtirol (IT_BZ) — Netto-Multiplikator ("Netto in busta")'
                            : atActive
                                ? 'Österreich (AT) — KV-Brutto (14 Gehälter: Fachkraft +52% / Lehrling +35%)'
                                : `Standard (${settings.selectedCountryId || 'DE'}) — Brutto + Lohnnebenkosten`}
                    </span>
                    {itBzActive && (
                    <button
                        type="button"
                        onClick={() => setItBzInfoModalOpen(true)}
                        title="Lohnberechnung & Lohnfaktor Südtirol (IT_BZ) — Systematik & Nachweis"
                        className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#dcb68a]/50 bg-[#dcb68a]/15 hover:bg-[#dcb68a] text-[#dcb68a] hover:text-black text-[10px] font-black uppercase tracking-wider transition-all"
                    >
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] leading-none">i</span>
                        <span>Systematik IT_BZ</span>
                    </button>
                    )}
                </div>
            </div>

            <div className="w-full max-w-[1920px] mx-auto flex flex-col gap-6 mb-12 px-4">
                <PersonalTable title="Produktive Mitarbeiter" dataKey="personalData" data={personalData} columns={prodColsDefinition} newRowTemplate={newProd} currency={currency} gehaltEingabeModus={gehaltEingabeModus} onGehaltEingabeModusChange={setGehaltEingabeModus} itBzActive={itBzActive} atActive={atActive} onOpenItBzInfo={() => setItBzInfoModalOpen(true)} />
                <PersonalTable title="Unproduktive Mitarbeiter" dataKey="unproductivePersonalData" data={unproductivePersonalData} columns={colsUnprod} newRowTemplate={newUnprod} currency={currency} gehaltEingabeModus={gehaltEingabeModus} onGehaltEingabeModusChange={setGehaltEingabeModus} itBzActive={itBzActive} atActive={atActive} onOpenItBzInfo={() => setItBzInfoModalOpen(true)} />
                <PersonalTable title="Azubis & Assistenz" dataKey="azubiAssistenzData" data={azubiAssistenzData} columns={colsAzubi} newRowTemplate={newAzubi} currency={currency} gehaltEingabeModus={gehaltEingabeModus} onGehaltEingabeModusChange={setGehaltEingabeModus} itBzActive={itBzActive} atActive={atActive} onOpenItBzInfo={() => setItBzInfoModalOpen(true)} />
            </div>
            
            <div className="w-full max-w-7xl mx-auto flex justify-center pb-20">
                <button onClick={handleSaveAndSync} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-12 rounded shadow-lg hover:shadow-blue-500/30 transform hover:-translate-y-1 transition-all border border-blue-400">Aktualisieren & Speichern</button>
            </div>

            <PETeamBonusModal
                isOpen={teamBonusModalOpen}
                onClose={() => setTeamBonusModalOpen(false)}
                currencySymbol={currencySymbol}
            />

            <ItBzLohnberechnungInfoModal
                isOpen={itBzInfoModalOpen}
                onClose={() => setItBzInfoModalOpen(false)}
            />
        </div>
    );
};
export default PersonalDatenPage;